// https://hello.atlassian.net/wiki/spaces/~474271760/pages/2427503547/Solution+Approach+Use+of+aggregation+from+CCP+search+on+customer+addresses
// In this component, following things are done:
// 1. For aggregation of ship to parties, we are using CCP search.
// 2. On expanding row, we are calling CCP search again.
// 3. On successful editing/adding customer address, we are collapsing all expanded rows.
import React, { useCallback, useState } from 'react';
import styled from '@emotion/styled';
import { token } from '@atlaskit/tokens';
import { Link } from 'react-router-dom';
import { ApolloError, ApolloQueryResult } from '@apollo/client';
import { EventAttributes } from '@atlassiansox/analytics-web-client';
import {
  ColGroup,
  CustomSearch,
  ExpandableRow,
  OpenSidebarButton,
  TableBody,
  TableHead,
  useFeatureToggle,
} from '@css-xp/base';
import isEmail from 'validator/lib/isEmail';
import { CustomDynamicResultTable } from '@css-xp/base/custom-dynamic-result-table';
import {
  AnalyticsTrackAction,
  AnalyticsTrackSubject,
  sendAnalyticsEvent,
  trackSearchEvent,
} from '@atlassian/temp-analytics';
import Tooltip, { TooltipPrimitive } from '@atlaskit/tooltip';
import { abgGraphqlClient, ProxyGraphClients } from '@atlassian/temp-client-utils';
import { CommerceAddress, Maybe } from '@css-xp/graphtypes/src/generated-types-new';
import { COMMERCIAL_PRICE_ELIGIBILITY, ENABLE_DESIGN_ITERATION_1, routePaths } from '@css-xp/ppc-constants';
import { getCompanyNameRoute, getRouteUrl } from '@css-xp/ppc-utils';
import { DEFAULT_ROWS_PER_PAGE } from '@atlassian/cssxp-utils-entitlement';
import AddIcon from '@atlaskit/icon/glyph/add';
import { emptyStateProp, errorStateProp, tableErrorMessage } from '@css-xp/ppc-common';
import {
  AggregatedShipToParty,
  AggregatedShipToPartyConnection,
  AggregatedShipToPartyOptions,
  FetchShipToPartiesForTransactionAccountPpcQuery,
  FetchShipToPartiesForTransactionAccountPpcQueryVariables,
  PartnerSearchOptions,
} from '@css-xp/ppc-graphtypes';
import { ADVOCATE_ACCESS_TYPE, ADVOCATE_RESOURCE_TYPE, AdvocateAccessWall } from '@css-xp/ppc-access-control';
import { emptyStatePropForSoldToAddressTable, emptyStatePropForWrongSearch } from '@css-xp/ppc-common/src/state-props';
import LockIcon from '@css-xp/base/assets/img/icon-lock.svg';
import { fetchShipToPartiesForTransactionAccountPPC } from '../../../queries';
import {
  ColumnList,
  extractPriceEligibility,
  formatAddressLabel,
  FormData,
  getHeadersForTable,
  getTaxExemptStatus,
  SIDEBAR,
  sidebarType,
} from './sold-to-address-table';
import './sold-to-address-table.scss';

export interface SoldToAddressTableProps {
  addressResponse?: Maybe<AggregatedShipToPartyConnection>;
  tableLoading: boolean;
  searchTerm?: string;
  transactionAccountId: string;
  tableRefetch: (
    variables:
      | Partial<{
          aggregatedShipToPartyOptions: AggregatedShipToPartyOptions;
        }>
      | undefined
  ) => Promise<ApolloQueryResult<unknown>>;
  billToAddress?: CommerceAddress | null;
  hidePricingEligibility?: boolean;
  showAddAddress?: boolean;
  currentPage?: number;
  rowsPerPage?: number;
  onRowChange?: (arg0: number) => void;
  onPageChange?: (arg0: number) => void;
  hideActions?: boolean;
  showSearchBox?: boolean;
  hideEdit?: boolean;
  onSearch?: (value: string) => void;
  hideAddFromBillToAddress?: boolean;
  showAddAddressInRow?: boolean;
  error?: string | ApolloError;
  searchOptions?: PartnerSearchOptions;
  emptyState?: any;
  heading?: React.ReactNode;
  isOrgSearchEnabled?: boolean;
}

const InlineDialog = styled(TooltipPrimitive)`
  background: white;
  border-radius: ${token('border.radius', '4px')};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  box-sizing: content-box;
  padding: ${token('space.100', '8px')} ${token('space.150', '12px')};
  max-width: 330px;
`;

const EditSoldToAddressDisabledMessage = () => (
  <div>
    This sold-to address cannot be edited due to the price eligibility applied to it. For more information please reach
    out to Partner Service Desk.
  </div>
);

export const SoldToAddressTableV2 = ({
  addressResponse,
  tableLoading,
  searchTerm,
  transactionAccountId,
  billToAddress,
  rowsPerPage,
  currentPage,
  hidePricingEligibility = false,
  showAddAddress = false,
  hideActions = false,
  showSearchBox = true,
  onRowChange,
  onPageChange,
  onSearch,
  tableRefetch,
  hideEdit = false,
  hideAddFromBillToAddress,
  showAddAddressInRow = false,
  error,
  searchOptions,
  emptyState = emptyStateProp,
  heading,
  isOrgSearchEnabled,
}: SoldToAddressTableProps) => {
  const [isEnabled] = useFeatureToggle();
  const [subRows, setSubRows] = useState<Map<string, CommerceAddress[]>>(new Map());
  const [companyNameToSubRowsLoading, setCompanyNameToSubRowsLoading] = useState<Map<string, boolean>>(new Map());
  const [expandedRows, setExpandedRows] = useState<Map<string, boolean>>(new Map()); // Map to keep track of expanded rows for each company name
  const [isSearched, setIsSearched] = useState(false);
  const isEmailTerm = searchTerm && isEmail(searchTerm);
  const totalCount = addressResponse?.totalCount || 0;
  const shipToParties = addressResponse?.nodes;

  const hasNoResults = totalCount === 0;

  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);
  const isDesignIteration1EnabledAndNoResult = isDesignIteration1Enabled && hasNoResults;

  const ppcBffGraphqlClient = React.useMemo(() => abgGraphqlClient(ProxyGraphClients.PPC_BFF), []);

  const buildShipToPartyOptions = (companyName: string) => {
    return {
      pageOptions: {
        cursor: null,
        size: 100,
      },
      searchOptions: searchOptions,
      filterOptions: {
        names: [companyName],
      },
    };
  };

  const renderActionsOnTop = useCallback(() => {
    return [
      () =>
        showAddAddress ? (
          <div className="add-sold-to-address add">
            <div className="add-action">
              <AdvocateAccessWall accessType={ADVOCATE_ACCESS_TYPE.CREATE} resource={ADVOCATE_RESOURCE_TYPE.CUSTOMER}>
                <OpenSidebarButton
                  sidebar={SIDEBAR.ADD_SOLD_TO_ADDRESS}
                  sidebarOptions={{
                    sidebarWidth: '600px',
                    isOverlay: true,
                  }}
                  payload={{
                    transactionAccountId,
                    billToAddress,
                    refetch: tableRefetch,
                    onSubmitComplete: () =>
                      handleOnSidebarSubmitCompleted(
                        AnalyticsTrackAction.SUCCESSFUL,
                        AnalyticsTrackSubject.NEW_CUSTOMER,
                        { transactionAccountId }
                      ),
                    onSubmitFailure: () =>
                      sendAnalyticsEvent(AnalyticsTrackAction.FAILED, AnalyticsTrackSubject.NEW_CUSTOMER, {
                        transactionAccountId,
                      }),
                    beforeSubmit: () =>
                      sendAnalyticsEvent(AnalyticsTrackAction.ADDED, AnalyticsTrackSubject.NEW_CUSTOMER, {
                        transactionAccountId,
                      }),
                    hidePricingEligibility,
                    sidebarType,
                    hideAddFromBillToAddress,
                    contentString: {
                      HEADING: 'Add customer',
                      SUBMIT_BUTTON: 'Add customer',
                      NOTIFICATION_SUCCESS_HEADING: 'Customer added',
                    },
                  }}
                  appearance="default"
                  testId="add-sold-to-address"
                  className="add-customer-btn"
                  {...(isDesignIteration1EnabledAndNoResult
                    ? { iconBefore: <AddIcon label="add" size="small" /> }
                    : {})}
                >
                  {isDesignIteration1EnabledAndNoResult ? 'Add customer' : 'Add new customer'}
                </OpenSidebarButton>
              </AdvocateAccessWall>
            </div>
          </div>
        ) : (
          <div />
        ),
    ];
  }, [transactionAccountId, billToAddress]);

  const renderAddNewSoldToAddressInRow = (shipToParty?: Maybe<CommerceAddress>) => {
    return showAddAddressInRow ? (
      <AdvocateAccessWall accessType={ADVOCATE_ACCESS_TYPE.CREATE} resource={ADVOCATE_RESOURCE_TYPE.SHIP_TO_ADDRESS}>
        <OpenSidebarButton
          sidebar={SIDEBAR.ADD_SOLD_TO_ADDRESS}
          payload={{
            shipToAddress: {
              name: shipToParty?.name,
            },
            transactionAccountId,
            billToAddress,
            pricingEligibility: shipToParty?.priceEligibility,
            refetch: tableRefetch,
            onSubmitComplete: () =>
              handleOnSidebarSubmitCompleted(
                AnalyticsTrackAction.SUCCESSFUL,
                AnalyticsTrackSubject.CUSTOMER_SOLD_TO_ADDRESS,
                { transactionAccountId }
              ),
            onSubmitFailure: () =>
              sendAnalyticsEvent(AnalyticsTrackAction.FAILED, AnalyticsTrackSubject.CUSTOMER_SOLD_TO_ADDRESS, {
                transactionAccountId,
              }),
            beforeSubmit: () =>
              sendAnalyticsEvent(AnalyticsTrackAction.ADDED, AnalyticsTrackSubject.CUSTOMER_SOLD_TO_ADDRESS, {
                transactionAccountId,
              }),
            hidePricingEligibility,
            contentString: {
              HEADING: 'Add sold-to address',
              SUBMIT_BUTTON: 'Add sold-to address',
              NOTIFICATION_SUCCESS_HEADING: 'Sold-to address added',
            },
            isAddType: true,
          }}
          appearance="link"
          sidebarOptions={{
            isOverlay: true,
          }}
          testId="add-ship-to-party-action"
          spacing="none"
        >
          Add sold-to address
        </OpenSidebarButton>
      </AdvocateAccessWall>
    ) : (
      <div />
    );
  };

  const handleOnSearch = useCallback(
    async (formData: FormData) => {
      const searchInput = formData.searchTerm;
      setIsSearched(!!searchInput);
      if (onSearch) {
        onSearch(searchInput);
      }
      trackSearchEvent();
    },
    [tableRefetch, onSearch]
  );

  const renderFilters = (showSearchBox: boolean) => {
    if (!showSearchBox) {
      return <div />;
    }
    return (
      <div className="sold-to-table-filter">
        <CustomSearch
          placeholder="Search"
          onSearch={handleOnSearch}
          isLoading={tableLoading}
          isClearable={true}
          helperMessage="Enter company name or sold-to address"
        />
      </div>
    );
  };

  const getColumns = useCallback(
    (hideActions: boolean, hideEdit: boolean, showAdd: boolean) => {
      return getHeadersForTable(hideActions, hideEdit, showAdd);
    },
    [hideActions, hideEdit, showAddAddressInRow]
  );

  const handleOnSidebarSubmitCompleted = (action: string, actionSubject: string, attributes?: EventAttributes) => {
    sendAnalyticsEvent(action, actionSubject, attributes);
    setExpandedRows(new Map());
  };

  const getColumnData = (visibleColumns: ColumnList[], item: any, isSecondaryRow = false) => {
    const customerAddressPath = getRouteUrl(routePaths.customerAddressDetailsById, {
      txaId: transactionAccountId,
      addressId: item.id,
    });
    const companyNamePath = getCompanyNameRoute({ txaId: transactionAccountId, companyName: item.name });

    return visibleColumns.map((column) => {
      switch (column.id) {
        case 'companyName':
          return (
            <td>
              <Link
                to={companyNamePath}
                data-testid="company-name-link"
                className="company-name"
                onClick={(e) => e.stopPropagation()}
              >
                {item.name}
              </Link>
            </td>
          );
        case 'address':
          return (
            <td>
              <Link to={customerAddressPath} data-testid="customer-address-link" onClick={(e) => e.stopPropagation()}>
                {formatAddressLabel(item)}
              </Link>
            </td>
          );
        case 'pricingEligibility':
          return <td>{extractPriceEligibility(item)}</td>;
        case 'taxExempt':
          return <td>{getTaxExemptStatus(item)}</td>;
        case 'edit':
          return (
            <>
              {isSecondaryRow ? <td /> : null}
              <td>
                {!hideEdit &&
                  (item?.priceEligibility?.toLowerCase() === COMMERCIAL_PRICE_ELIGIBILITY.toLowerCase() ? (
                    <AdvocateAccessWall
                      accessType={ADVOCATE_ACCESS_TYPE.UPDATE}
                      resource={ADVOCATE_RESOURCE_TYPE.SHIP_TO_ADDRESS}
                    >
                      <OpenSidebarButton
                        sidebar={SIDEBAR.EDIT_SOLD_TO_ADDRESS}
                        payload={{
                          shipToAddress: item,
                          transactionAccountId,
                          billToAddress,
                          pricingEligibility: item?.shipToPriceEligibility,
                          refetch: tableRefetch,
                          hidePricingEligibility,
                          sidebarType,
                          onSubmitComplete: () =>
                            handleOnSidebarSubmitCompleted(
                              AnalyticsTrackAction.SUCCESSFUL,
                              AnalyticsTrackSubject.SOLD_TO_ADDRESS,
                              { transactionAccountId }
                            ),
                          onSubmitFailure: () =>
                            sendAnalyticsEvent(AnalyticsTrackAction.FAILED, AnalyticsTrackSubject.SOLD_TO_ADDRESS, {
                              transactionAccountId,
                            }),
                          beforeSubmit: () =>
                            sendAnalyticsEvent(AnalyticsTrackAction.UPDATED, AnalyticsTrackSubject.SOLD_TO_ADDRESS, {
                              transactionAccountId,
                            }),
                        }}
                        appearance="link"
                        sidebarOptions={{
                          isOverlay: true,
                          sidebarWidth: '600px',
                        }}
                        testId="edit-ship-to-party-action"
                        spacing="none"
                      >
                        Edit
                      </OpenSidebarButton>
                    </AdvocateAccessWall>
                  ) : (
                    <Tooltip
                      testId="sold-to-address-edit-disabled-tooltip"
                      component={InlineDialog}
                      content={<EditSoldToAddressDisabledMessage />}
                    >
                      {/* @ts-ignore */}
                      <img src={LockIcon} data-testid="lock-icon" />
                    </Tooltip>
                  ))}
              </td>
            </>
          );
        case 'add':
          return <td>{renderAddNewSoldToAddressInRow(item)}</td>;
      }
    });
  };

  const getSubRowsForCompanyName = useCallback(
    (companyName: string, index: number) => {
      const subRowsForCompanyName = subRows.get(companyName) || [];
      // sorting for subRows based on addressLine1 to maintain the same order on UI as well as exported CSV
      subRowsForCompanyName.sort((a, b) => (a?.addressLine1 ?? '').localeCompare(b?.addressLine1 ?? '') ?? 0);
      return subRowsForCompanyName.map((shipToParty) => {
        return (
          <tr
            data-testid={`ship-to-table-row-${shipToParty?.id}`}
            key={`ship-to-table-row-${index}-${shipToParty?.id}`}
            className="grouped-child"
          >
            {getColumnData(getColumns(hideActions, hideEdit, false), shipToParty, true)}
          </tr>
        );
      });
    },
    [subRows]
  );

  const getSubRowsLoadingForCompanyName = useCallback(
    (companyName: string) => {
      return companyNameToSubRowsLoading.get(companyName) ?? true;
    },
    [companyNameToSubRowsLoading]
  );

  const handleOnExpandRow = async (shipToPartyGroup: AggregatedShipToParty) => {
    const companyName = shipToPartyGroup.key;
    setExpandedRows((map) => new Map(map.set(companyName, true)));

    setCompanyNameToSubRowsLoading((map) => new Map(map.set(companyName, true)));
    const shipToPartiesResponse = await ppcBffGraphqlClient.query<
      FetchShipToPartiesForTransactionAccountPpcQuery,
      FetchShipToPartiesForTransactionAccountPpcQueryVariables
    >({
      query: fetchShipToPartiesForTransactionAccountPPC,
      variables: {
        id: transactionAccountId,
        shipToPartyOptions: buildShipToPartyOptions(companyName),
      },
      fetchPolicy: 'network-only',
    });
    const subLoading = shipToPartiesResponse.loading;
    setCompanyNameToSubRowsLoading((map) => new Map(map.set(companyName, subLoading)));
    if (!subLoading) {
      setSubRows(
        (map) => new Map(map.set(companyName, shipToPartiesResponse.data?.transactionAccount.shipToParty?.nodes ?? []))
      );
    }
  };

  const renderRowWithGroupedData = (index: number, shipToPartyGroup: AggregatedShipToParty) => {
    if (shipToPartyGroup?.data?.length === 1) {
      return (
        <tr
          data-testid={`ship-to-table-row-${index}`}
          key={`ship-to-table-row-${index}-${shipToPartyGroup?.key}`}
          className="ungrouped-child"
        >
          {getColumnData(getColumns(hideActions, hideEdit, showAddAddressInRow), shipToPartyGroup.data[0])}
        </tr>
      );
    }
    const companyNamePath = getCompanyNameRoute({ txaId: transactionAccountId, companyName: shipToPartyGroup.key });

    const rows = [
      [
        <td key="1">
          <Link to={companyNamePath} className="company-name" onClick={(e) => e.stopPropagation()}>
            {shipToPartyGroup.key}
          </Link>
        </td>,
        <td key="2" />,
        <td key="3" />,
        <td key="4" />,
        <td key="5">{renderAddNewSoldToAddressInRow(shipToPartyGroup?.data && { name: shipToPartyGroup.key })}</td>,
      ],
    ];

    const shipToPartyGroupKey = shipToPartyGroup.key;
    return (
      <ExpandableRow
        key={index}
        rowsAsCells={rows}
        subRows={getSubRowsForCompanyName(shipToPartyGroupKey, index)}
        isSubRowsLoading={getSubRowsLoadingForCompanyName(shipToPartyGroupKey)}
        isDefaultExpanded={expandedRows.get(shipToPartyGroupKey)}
        onExpand={async () => {
          await handleOnExpandRow(shipToPartyGroup);
        }}
        onCollapse={() => {
          setExpandedRows((map) => new Map(map.set(shipToPartyGroupKey, false)));
        }}
      />
    );
  };

  const renderTableBody = (shipToParties: Maybe<AggregatedShipToParty>[] | null | undefined) => {
    return (shipToParties ?? [])?.map((item, index) => renderRowWithGroupedData(index, item as AggregatedShipToParty));
  };

  const emptyStatePropsForSearch = isDesignIteration1Enabled ? emptyStatePropForWrongSearch : emptyState;

  const emptyStateProps = isDesignIteration1Enabled
    ? emptyStatePropForSoldToAddressTable(renderActionsOnTop())
    : emptyState;

  return (
    <div className="sold-to-address-container">
      <CustomDynamicResultTable
        innerHeading={heading}
        testId="shipping-details"
        isLoading={tableLoading}
        totalCount={totalCount}
        noResults={hasNoResults}
        isPaginationEnabled={isEmailTerm || isOrgSearchEnabled ? false : totalCount > DEFAULT_ROWS_PER_PAGE}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        onRowChange={onRowChange}
        selectableResults={true}
        error={error}
        actions={renderActionsOnTop()}
        boxShadow={true}
        className="sold-to-address-table"
        filters={renderFilters(showSearchBox)}
        shouldForceEnableFilters={true}
        emptyStateProps={isSearched ? emptyStatePropsForSearch : emptyStateProps}
        errorStateProps={errorStateProp}
        isSearched={isSearched}
      >
        <ColGroup columns={getColumns(hideActions, hideEdit, showAddAddressInRow)} />
        <TableHead columns={getColumns(hideActions, hideEdit, showAddAddressInRow)} />
        <TableBody>{renderTableBody(shipToParties)}</TableBody>
      </CustomDynamicResultTable>
    </div>
  );
};
